/* eslint-disable react/no-danger */
import {Grid, GridItem, Heading} from '@constellation/core';
import {memo} from 'react';
import {type cardPaymentProps} from '../../../../../types/cardPaymentServiceDetails.js';
import {
  EXAMPLE,
  SCHEME_MASTERCARD,
  SCHEME_VISA,
} from '../../../../../utils/constants/public/psr.js';
import {getPsrLabel} from '../../../../../utils/index.js';
import {
  getCardRatePercent,
  getPercent,
  getQuotationPercent,
  getQuotationPrice,
  getTotalCardRatePercent,
  getTotalPercent,
} from '../../../../../utils/quotation.js';
import {
  ExampleDiv,
  ExampleGrid,
  ExampleGridItem,
  ExampleGridItemHeading,
  ExampleGridMarginBottom,
  ExampleLargeLine,
  ExampleRowOneText,
  ExampleRowTwoText,
  ExampleSmallLine,
  ExampleValuesGrid,
  GridItemEven,
  OtherSectionText,
} from '../Styles/Styles.js';

const Example = memo((props: cardPaymentProps) => {
  const schemeRates = props.quoteResponse?.quote?.schemeRates;
  const authorisationFee =
    props.quoteResponse?.quote?.otherTransactionFees?.authorisationFee;
  const mastercardRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_MASTERCARD
  );
  const visaRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_VISA
  );
  const vdFace = visaRates?.debit?.face;
  const mcPhoneOnline =
    mastercardRates?.credit?.online || mastercardRates?.credit?.moto;
  const vdFaceTotal = getTotalCardRatePercent(vdFace, authorisationFee?.value);
  const mcPhoneOnlineTotal = getTotalCardRatePercent(
    mcPhoneOnline,
    authorisationFee?.value
  );

  return (
    <div aria-describedby={EXAMPLE} role='table'>
      <ExampleDiv marginBottom='05' marginTop='05'>
        <Heading as='h2' marginBottom='01' size='s3'>
          <div id={EXAMPLE}>
            {getPsrLabel('cardPaymentServicesDetails.example.labelText')}
          </div>
        </Heading>
        <ExampleLargeLine
          aria-hidden={true}
          marginBottom='none'
          marginTop='none'
        />
        <div role='rowgroup'>
          <Grid alignY='center'>
            <ExampleGridMarginBottom xs={12}>
              <div role='row'>
                <ExampleGrid>
                  <ExampleGridItemHeading md={6} sm={12}>
                    <ExampleRowOneText>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getPsrLabel(
                            'cardPaymentServicesDetails.example.row1'
                          ),
                        }}
                        role='rowheader'
                      />
                    </ExampleRowOneText>
                    <ExampleSmallLine marginBottom='none' marginTop='none' />
                  </ExampleGridItemHeading>
                  <GridItem md={6} sm={12}>
                    <ExampleValuesGrid>
                      <GridItemEven md={4} xs={4}>
                        <div role='cell'>
                          <OtherSectionText weight='bold'>
                            {getPercent(getCardRatePercent(vdFace))}
                          </OtherSectionText>
                          <br />
                          <OtherSectionText>
                            <small>
                              (
                              {`${getQuotationPercent(
                                vdFace
                              )} of ${getQuotationPrice(10.0, true)}`}
                              )
                            </small>
                          </OtherSectionText>
                        </div>
                      </GridItemEven>
                      <ExampleGridItem md={4} xs={4}>
                        <div role='cell'>
                          <OtherSectionText weight='bold'>
                            +{getTotalPercent(authorisationFee?.value)}
                          </OtherSectionText>
                          <br />
                          <OtherSectionText>
                            <small>
                              (
                              {getPsrLabel(
                                'cardPaymentServicesDetails.example.feeSubText'
                              )}
                              )
                            </small>
                          </OtherSectionText>
                        </div>
                      </ExampleGridItem>
                      <GridItemEven md={4} xs={4}>
                        <OtherSectionText weight='bold'>
                          <div role='cell'>={getPercent(vdFaceTotal)}</div>
                        </OtherSectionText>
                      </GridItemEven>
                    </ExampleValuesGrid>
                  </GridItem>
                </ExampleGrid>
              </div>
              <ExampleLargeLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='none'
              />
            </ExampleGridMarginBottom>

            <GridItem xs={12}>
              <div role='row'>
                <ExampleGrid>
                  <ExampleGridItemHeading md={6} sm={12}>
                    <ExampleRowTwoText>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: getPsrLabel(
                            'cardPaymentServicesDetails.example.row2'
                          ),
                        }}
                        role='rowheader'
                      />
                    </ExampleRowTwoText>
                    <ExampleSmallLine marginBottom='none' marginTop='none' />
                  </ExampleGridItemHeading>
                  <GridItem md={6} sm={12}>
                    <ExampleValuesGrid>
                      <GridItemEven md={4} xs={4}>
                        <div role='cell'>
                          <OtherSectionText weight='bold'>
                            {getPercent(getCardRatePercent(mcPhoneOnline))}
                          </OtherSectionText>
                          <br />
                          <OtherSectionText>
                            <small>
                              (
                              {`${getQuotationPercent(
                                mcPhoneOnline
                              )} of ${getQuotationPrice(10.0, true)}`}
                              )
                            </small>
                          </OtherSectionText>
                        </div>
                      </GridItemEven>
                      <ExampleGridItem md={4} xs={4}>
                        <div role='cell'>
                          <OtherSectionText weight='bold'>
                            +{getTotalPercent(authorisationFee?.value)}
                          </OtherSectionText>
                          <br />
                          <OtherSectionText>
                            <small>
                              (
                              {getPsrLabel(
                                'cardPaymentServicesDetails.example.feeSubText'
                              )}
                              )
                            </small>
                          </OtherSectionText>
                        </div>
                      </ExampleGridItem>
                      <GridItemEven md={4} xs={4}>
                        <OtherSectionText weight='bold'>
                          <div role='cell'>
                            ={getPercent(mcPhoneOnlineTotal)}
                          </div>
                        </OtherSectionText>
                      </GridItemEven>
                    </ExampleValuesGrid>
                  </GridItem>
                </ExampleGrid>
              </div>
              <ExampleLargeLine
                aria-hidden={true}
                marginBottom='none'
                marginTop='none'
              />
            </GridItem>
          </Grid>
        </div>
      </ExampleDiv>
    </div>
  );
});

export default Example;
